/**
 * 宿主（小程序）在 webview URL 上显式声明的登录态参数名。
 *
 * 背景：小程序退出登录只会清掉小程序侧的 otp / uid，webview 内的 sessionUid、cookie
 * 以及服务端联登会话都不会失效（登出接口是小程序维度，cookie 上下文与 webview 隔离）。
 * 因此 H5 无论读缓存还是问接口，都会拿回旧 uid 而误判为已登录。唯一可信的结论只能由
 * 宿主给出，小程序在拼 webview 地址时把真实登录态写在这个参数上：
 *   1 - 宿主已登录（URL 上同时带 uid / code，沿用原有判定逻辑）
 *   0 - 宿主未登录（H5 必须强制游客态，且不得再向后端探测）
 * 参数缺失 = APP / 外部浏览器 / 未升级的小程序，一律沿用原有逻辑，保证零回归。
 *
 * 为什么 cookie 是关键污染源：wasxuid_r / xp-uid 是 .xiaopeng.com 维度的持久 cookie，
 * 跨 web-view 实例共享。H5 侧的 goToPage / goToPageV2 拼小程序跳转时 appId 恒为空，
 * 这类跳转完全绕过小程序的 getCode() 登录门禁，新开的 web-view 里 sessionStorage 是
 * 空的，但 cookie 还在——于是退登后从游客态专题页跳到其它充电 H5 就会串用上一个用户的
 * 登录态。因此本模块除了判定，还负责把这份残留清掉 / 旁路掉。
 *
 * 本模块不依赖任何其它业务模块：它需要在入口最早处执行，任何 import 都可能把
 * 更重的模块图和副作用提前引入。isValidUid / readCookie 因此内联了一份实现。
 */
export const HOST_LOGIN_STATE_KEY = "hostLoginState";

/** 宿主明确声明"未登录"的取值 */
const HOST_LOGGED_OUT_VALUE = "0";

/** 宿主在 URL 上声明权威身份的参数名（小程序 getWebviewUrl 拼入） */
const HOST_UID_KEY = "uid";

/** webview 内承载身份的 cookie，由后端在 .xiaopeng.com 上下发 */
const IDENTITY_COOKIE_KEYS = ["xp-uid", "wasxuid_r"];

/** 身份缓存的 sessionStorage key，由联登 SDK 的 processGetParams 写入 */
const SESSION_UID_KEY = "sessionUid";

/**
 * 结论缓存：同一次页面加载内 URL 不会变，避免反复解析。
 * 用内存态而不是 sessionStorage，避免又引入一份需要被清理的脏缓存。
 */
let hostLoggedOut: boolean | null = null;

/**
 * 从地址里读取指定参数。
 * 小程序是拼在 hash 之前的 search 段（见 webpage 页的 getWebviewUrl），这里同时兼容
 * hash 里的写法，所以直接在整个 href 上匹配。
 *
 * 必须用这个正则而不是 href.split("?")[1] 那类做法：hash 段自身也可能带 "?"
 * （形如 par-marketing.html?uid=1&hostLoginState=0#configPage?pageCode=x），
 * 按 "?" 切分会把 "#configPage" 混进参数值里。[^&#]* 显式排除了 "#"。
 */
const readParamFromHref = (href: string, key: string): string => {
  const matched = href.match(new RegExp(`[?&]${key}=([^&#]*)`));
  return matched ? matched[1] : "";
};

/**
 * 宿主是否已明确告知"当前未登录"。
 * 只有拿到 hostLoginState=0 时才为 true，其余情况（包括参数缺失）均为 false。
 */
export const isHostLoggedOut = (): boolean => {
  if (hostLoggedOut === null) {
    hostLoggedOut =
      typeof window !== "undefined" &&
      readParamFromHref(window.location.href, HOST_LOGIN_STATE_KEY) ===
        HOST_LOGGED_OUT_VALUE;
  }
  return hostLoggedOut;
};

/**
 * uid 有效性校验：与 api/common.ts 的 isValidUid 同规则。
 * "0" 是联登 SDK 在 APP 未登录时写入的占位值，"-1" 是小程序退登后写入本地的占位值，
 * 都不是真实 uid。此处内联而非 import，是为了保持本模块零业务依赖。
 */
const isValidUid = (uid?: string | null): boolean => {
  const value = String(uid ?? "").trim();
  return value !== "" && value !== "0" && value !== "-1";
};

/** 宿主在 URL 上声明的权威 uid，无声明或无效时返回空串 */
const readHostUid = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const raw = readParamFromHref(window.location.href, HOST_UID_KEY);
  if (!raw) {
    return "";
  }
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // 保留原值：uid 是纯数字串，正常不会触发
  }
  return isValidUid(decoded) ? decoded : "";
};

const readCookie = (name: string): string => {
  if (typeof document === "undefined") {
    return "";
  }
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const cookie of cookies) {
    const index = cookie.indexOf("=");
    if (index > -1 && cookie.slice(0, index) === name) {
      try {
        return decodeURIComponent(cookie.slice(index + 1));
      } catch {
        return cookie.slice(index + 1);
      }
    }
  }
  return "";
};

/** 当前 webview 内缓存的身份（sessionStorage 优先，其次 cookie） */
const readCachedIdentity = (): string => {
  try {
    const fromSession = sessionStorage.getItem(SESSION_UID_KEY);
    if (fromSession) {
      return fromSession;
    }
  } catch {
    // 隐私模式等场景读不到 sessionStorage，继续看 cookie
  }
  for (const key of IDENTITY_COOKIE_KEYS) {
    const value = readCookie(key);
    if (value) {
      return value;
    }
  }
  return "";
};

/**
 * 清掉 webview 内残留的身份缓存。
 *
 * cookie 是"尽力删除"：domain / path 必须与后端 set-cookie 时完全一致才删得掉，
 * 线上实际属性未逐一核实。因此正确性不依赖这里，由 resolveUid 的优先级兜住
 * （宿主声明高于任何本地缓存，删不掉也读不到）；这里只是顺手做的卫生措施，
 * 避免脏 cookie 长期留在 webview 里影响其它未接入契约的页面。
 */
const clearCachedIdentity = (): void => {
  try {
    sessionStorage.removeItem(SESSION_UID_KEY);
  } catch (error) {
    console.warn("[host-login-state] 清理 sessionUid 失败", error);
  }
  if (typeof document === "undefined") {
    return;
  }
  const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  IDENTITY_COOKIE_KEYS.forEach((key) => {
    try {
      // 同时尝试当前域与 .xiaopeng.com：后端下发的是哪一个未核实，多写几条无副作用
      document.cookie = `${key}=; ${expires}; path=/`;
      document.cookie = `${key}=; ${expires}; path=/; domain=.xiaopeng.com`;
    } catch (error) {
      console.warn(`[host-login-state] 清理 cookie ${key} 失败`, error);
    }
  });
};

/**
 * 解析当前应当使用的 uid：宿主声明的身份优先于任何本地缓存。
 *
 * 不能写成 `getAuthoritativeUid() || sessionStorage... || cookie...` 那样的 `||` 链——
 * 宿主声明未登录时权威值为空串，`||` 会继续回退到 sessionStorage / cookie，等于没拦住。
 * 所以必须由本函数区分"宿主说未登录"（返回空，不回退）与"没有宿主信息"（回退原逻辑）。
 *
 * @param fallback 无宿主声明时的取值方式，即各调用点原有的 sessionStorage / cookie 链。
 *                 APP / 外部浏览器环境永远走这条分支，行为完全不变。
 */
export const resolveUid = (fallback: () => string): string => {
  if (isHostLoggedOut()) {
    return "";
  }
  const hostUid = readHostUid();
  if (hostUid) {
    return hostUid;
  }
  return fallback();
};

/**
 * 应用宿主登录态：让 webview 内的身份缓存与宿主声明对齐。
 *
 * 必须在入口最早处（initial() 之前，已收拢进 initial 的第一行）调用：sentry、track-id、
 * union-axios 的 formatHeader 都会读 sessionStorage / cookie，只有先在这里对齐，才能
 * 保证上报和请求头不会带上已登出用户或上一个账号的 uid。
 *
 * 两种需要清理的情况：
 *   1. 宿主已登出 —— 缓存里的一切都是退登前的残留；
 *   2. 宿主已登录但身份与缓存不一致 —— 切换账号场景，cookie 里还是上一个 uid。
 */
export const applyHostLoginState = (): void => {
  if (isHostLoggedOut()) {
    clearCachedIdentity();
    return;
  }
  const hostUid = readHostUid();
  if (!hostUid) {
    // 无宿主声明：APP / 外部浏览器 / 未升级的小程序，不动任何缓存
    return;
  }
  const cached = readCachedIdentity();
  if (cached && cached !== hostUid) {
    clearCachedIdentity();
  }
};

/**
 * 去掉 hostLoginState 的当前地址，用于回传给宿主作为登录后的跳回地址。
 *
 * 不剔除会出现回环：H5 把带 hostLoginState=0 的 href 作为 webviewUrl 传给小程序登录页，
 * 登录成功后小程序原样拼回 webview 地址，H5 又判为未登录。
 * （小程序侧同时做了覆盖写兜底，这里是第二道防线）
 */
export const getHrefWithoutHostLoginState = (
  href: string = typeof window === "undefined" ? "" : window.location.href,
): string => {
  if (!href) {
    return href;
  }
  const hashIndex = href.indexOf("#");
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf("?");
  if (queryIndex === -1) {
    return href;
  }
  const path = beforeHash.slice(0, queryIndex);
  const kept = beforeHash
    .slice(queryIndex + 1)
    .split("&")
    .filter((item) => item && item.split("=")[0] !== HOST_LOGIN_STATE_KEY);
  return `${path}${kept.length ? `?${kept.join("&")}` : ""}${hash}`;
};
